import type { MouseEvent, ReactElement } from "react";
import { useEffect, useMemo } from "react";

import { NoBorderButtonWrap } from "@similarweb/ui-components";
import { Box } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { objectKeys } from "app/helpers/objects";
import { useSelectableTree } from "app/pages/sales-intelligence/hooks/useSelectableTree";
import {
    REVEAL_STATUS_DROPDOWN_WIDTH,
    StyledRevealStatusDropdown,
    StyledRevealStatusDropdownItem,
    StyledSimpleChipItemWrap,
} from "app/pages/sales-intelligence/pages/company-analysis/components/contacts/ContactRevealStatusDropdown/styles";
import { initialActionsPanelFilters } from "app/pages/sales-intelligence/sub-modules/contacts-search/constants/initialState";
import type { ActionsPanelFilters } from "app/pages/sales-intelligence/sub-modules/contacts-search/types/common";

import { revealStatusDropdownLabels, revealStatusDropdownTree } from "./const";
import type { IRevealStatusDropdownTreeNode } from "./type";

type Props = {
    isDisabled?: boolean;
    filters: Partial<ActionsPanelFilters>;
    onChange: (filters: Partial<ActionsPanelFilters>) => void;
};

const ContactRevealStatusDropdown = ({ filters, isDisabled, onChange }: Props) => {
    const getSelectedIds = (newFilters: Partial<ActionsPanelFilters>): string[] => {
        const ids = objectKeys(newFilters).filter(
            (filterKey) => newFilters[filterKey] === true && revealStatusDropdownLabels[filterKey],
        );
        // When children (includeOnlyExported/excludeExported) are selected, includeOnlyRevealed is
        // derived for API purposes. Exclude it from tree selection so parent shows partiallySelected.
        if (ids.some((id) => id === "includeOnlyExported" || id === "excludeExported")) {
            return ids.filter((id) => id !== "includeOnlyRevealed");
        }
        return ids;
    };

    const getInitialSelectedIds = (): string[] => getSelectedIds(filters);

    const { tree, allSelectedItems, setSelected, iterateTree, getUpdatedSelectionById, getSelectedList } =
        useSelectableTree<IRevealStatusDropdownTreeNode>(revealStatusDropdownTree, getInitialSelectedIds);

    useEffect(() => {
        setSelected(getSelectedIds(filters));
    }, [filters]);

    const handleSelect = (item: IRevealStatusDropdownTreeNode) => {
        const updatedTree = getUpdatedSelectionById(tree, item.id);
        const newSelectedList = getSelectedList(updatedTree);
        onChange(newSelectedList);
    };

    const handleClear = (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        onChange(initialActionsPanelFilters);
    };

    const text = useMemo(() => {
        const selectedIds = Object.keys(allSelectedItems);

        if (selectedIds.length === 0) {
            return "";
        }

        const label = translate(revealStatusDropdownLabels[selectedIds[0]]);

        if (selectedIds.length === 1) {
            return label;
        }

        return `${label} +${selectedIds.length - 1}`;
    }, [allSelectedItems]);

    const renderButton = () => {
        return text ? (
            <StyledSimpleChipItemWrap
                isDisabled={isDisabled}
                text={text}
                onCloseItem={handleClear}
            />
        ) : (
            <NoBorderButtonWrap
                disabled={isDisabled}
                key="selectedItem"
            >
                {translate("si.account-overview.filters.reveal-status.button-text")}
            </NoBorderButtonWrap>
        );
    };

    return (
        <Box
            id="contact-reveal-status-dropdown-container"
            position="relative"
        >
            <StyledRevealStatusDropdown
                appendTo="#contact-reveal-status-dropdown-container"
                buttonWidth="fit-content"
                noResultsMessage={translate("dropdown.no.results")}
                selectedIds={allSelectedItems}
                width={REVEAL_STATUS_DROPDOWN_WIDTH}
                dropdownPopupPlacement="bottom-left"
                closeOnItemClick={false}
                disabled={isDisabled}
                onClick={handleSelect}
            >
                {[
                    renderButton(),
                    ...iterateTree((item, depth): ReactElement => {
                        return (
                            <StyledRevealStatusDropdownItem
                                id={item.id}
                                depth={depth}
                                showCheckBox
                                partiallySelected={item.state === "partiallySelected"}
                            >
                                {translate(item.title)}
                            </StyledRevealStatusDropdownItem>
                        );
                    }),
                ]}
            </StyledRevealStatusDropdown>
        </Box>
    );
};

export default ContactRevealStatusDropdown;
